import "twin.macro";

import { Link } from "react-router";

import { Chip } from "@mui/material";

import { sortBy } from "lodash";

import { isOrderWindowActive } from "@features/orderWindows";
import { PaperTooltip } from "@features/ui";
import { OrderWindow } from "@models/OrderWindow";
import { Program } from "@models/Program";
import { formatUtcDate, utcDate } from "@utility/utilityFunctions";

import ProgramStatusChip from "../ProgramStatusChip";

const orderWindowLink = (programId: string, ow: OrderWindow) => (
  <Link
    to={`/programs/${programId}/order/${ow.id}`}
    target="_blank"
    tw="underline text-primary-600"
  >
    {ow.name}
  </Link>
);

const getOrderWindowStatus = (
  programId: string,
  orderWindows: OrderWindow[]
) => {
  const sortedOrderWindows = sortBy(orderWindows, "openDate");
  const activeOrderWindows = sortedOrderWindows.filter(isOrderWindowActive);

  if (orderWindows.length === 0) {
    return {
      text: "No order window assigned",
      tooltip: "Assign an order window to allow users to place orders.",
    };
  }

  if (activeOrderWindows.length > 0) {
    const latestOw = activeOrderWindows[0];
    return {
      text: "Open for Orders",
      tooltip: (
        <>
          Program is orderable until{" "}
          <b>{formatUtcDate(latestOw.closeDate, "PP")}</b> on{" "}
          <span>{orderWindowLink(programId, latestOw)}</span>.
        </>
      ),
    };
  }

  const nextUpcomingOw = sortedOrderWindows.find(
    (ow) => utcDate(ow.openDate) > new Date()
  );
  if (nextUpcomingOw) {
    return {
      text: "Coming up",
      tooltip: (
        <>
          Program is currently <b>not orderable</b>. The next order window opens{" "}
          <b>{formatUtcDate(nextUpcomingOw.openDate, "PP")}</b> on{" "}
          <span>{orderWindowLink(programId, nextUpcomingOw)}</span>.
        </>
      ),
    };
  }

  const lastOw = sortedOrderWindows
    .reverse()
    .find((ow) => utcDate(ow.closeDate) < new Date())!;
  if (lastOw) {
    return {
      text: "Closed for Orders",
      tooltip: (
        <>
          Program is currently <b>not orderable</b>. The last order window
          closed <b>{formatUtcDate(lastOw.closeDate, "PP")}</b> on{" "}
          <span>{orderWindowLink(programId, lastOw)}</span>.
        </>
      ),
    };
  }
  // This line should never be reached
  return { text: "Closed for Orders", tooltip: null };
};

const OrderWindowChip = ({
  programId,
  orderWindows,
}: {
  programId: string;
  orderWindows: OrderWindow[];
}) => {
  const { text, tooltip } = getOrderWindowStatus(programId, orderWindows);
  return (
    <PaperTooltip title={<p tw="text-base">{tooltip}</p>} arrow>
      <Chip
        label={text}
        color={text === "Open for Orders" ? "success" : "default"}
      />
    </PaperTooltip>
  );
};

const ProgramStatusChips = ({ program }: { program: Program }) => {
  return (
    <div tw="flex flex-wrap gap-2">
      <ProgramStatusChip program={program} />
      {program.status === "complete" && program.isActive && (
        <OrderWindowChip
          programId={program.id}
          orderWindows={program.orderWindows}
        />
      )}
    </div>
  );
};

export default ProgramStatusChips;
